import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import GoBack from 'components/actions/GoBack';
import {
    MdAdd,
    MdDisplaySettings,
    MdDoneOutline,
    MdOutlinePayment,
    MdOutlineRocketLaunch,
    MdOutlineWhatsapp,
} from 'react-icons/md';
import { getImage } from 'variables/images';
import { TbTargetArrow } from 'react-icons/tb';
import { useIndustries } from 'services/industryService';
import SignUpModal from 'views/admin/main/buyer/sellerOffer/components/SignUpModal';
import PayWithWalletButton from 'components/wallet/PayWithWalletButton';

type Palette = 'yellow' | 'green' | 'neutral';

const paletteStyles: Record<Palette, { wrap: string; icon: string; text: string }> = {
    yellow:  { wrap: 'bg-leadrs-yellow-soft', icon: 'text-leadrs-yellow-focus', text: 'text-leadrs-yellow-focus' },
    green:   { wrap: 'bg-leadrs-green-soft',  icon: 'text-leadrs-green-focus',  text: 'text-leadrs-green-focus'  },
    neutral: { wrap: 'bg-white border border-leadrs-stroke', icon: 'text-leadrs-text-soft', text: 'text-leadrs-text-soft' },
};

const InfoBox = (props: { icon?: any; title?: string; description?: string; palette?: Palette }) => {
    const { icon, title, description, palette = 'neutral' } = props;
    const s = paletteStyles[palette];
    return (
        <div className={`rounded-xl p-4 ${s.wrap}`}>
            <div className="mb-2 flex items-center gap-2">
                <span className={s.icon}>{icon}</span>
                <p className={`font-geist text-sm font-semibold ${s.text}`}>{title}</p>
            </div>
            <p className={`font-geist text-sm whitespace-pre-line ${s.text} opacity-80`}>{description}</p>
        </div>
    );
};

const BuyerMarketPlacePage = () => {
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const [user, setUser] = useState<any>(null);
    const [offer, setOffer] = useState<any>({});
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const params = useParams();
    const nav = useNavigate();

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                setUser(res.data);
                if (res.data && res.data.type !== 'buyer') {
                    window.location.href = '/my-request';
                }
            })
            .catch((err) => console.log('No user info found', err));
    };

    const getOffer = (id: string) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/seller-offer/marketplace/${id}`, config)
            .then((res) => setOffer(res.data))
            .catch((err) => console.log('No offer found', err));
    };

    useEffect(() => {
        if (!localStorage.getItem('token') || localStorage.getItem('token').length < 10)
            window.location.href = '/auth/sign-up';
        getOffer(params.id);
        initUser();
        // eslint-disable-next-line
    }, []);

    function capitalizeFirstLetter(str: string) {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    if (industriesLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-leadrs-brand" />
            </div>
        );
    }

    const industryInfo = industriesData.find((i: any) => i.name === offer?.industry);
    const isGuest = user?.email?.includes('guest-');

    return (
        <div>
            <GoBack />

            {isGuest && (
                <div className="mb-4 mt-2 rounded-xl bg-leadrs-yellow-soft p-4">
                    <p className="font-geist text-sm font-semibold text-leadrs-yellow-focus">
                        Vous utilisez actuellement un compte invite 🚧
                    </p>
                    <p className="mt-1 font-geist text-sm text-leadrs-yellow-focus opacity-80">
                        Pour ne rien perdre et acceder a toutes les fonctionnalites, rendez-vous dans vos{' '}
                        <strong>parametres de compte</strong> afin d'ajouter votre adresse e-mail.
                    </p>
                    <button
                        onClick={() => nav('/settings')}
                        className="mt-2 font-geist text-sm font-semibold text-leadrs-yellow-focus underline"
                    >
                        Mettre a jour mon compte &rarr;
                    </button>
                </div>
            )}

            <div className="mt-4 flex flex-col gap-6 lg:flex-row">
                {/* Left column — image + actions + for-who */}
                <div className="flex flex-col gap-4 lg:w-[380px] lg:flex-shrink-0">
                    {getImage(offer?.industry, offer?.subIndustry) ? (
                        <img
                            src={getImage(offer?.industry, offer?.subIndustry)}
                            className="w-full rounded-xl object-cover"
                            style={{ maxHeight: 260 }}
                            alt={`${offer?.industry} ${offer?.subIndustry}`}
                        />
                    ) : (
                        <div className="flex h-48 items-center justify-center rounded-xl bg-leadrs-stroke">
                            <p className="font-geist text-sm text-leadrs-text-soft">{offer?.industry}</p>
                        </div>
                    )}

                    <button
                        className="flex items-center justify-center gap-2 rounded-lg border border-leadrs-stroke bg-white px-5 py-3 font-geist text-sm font-medium text-leadrs-text transition-colors hover:border-leadrs-brand"
                        onClick={() => window.open('https://wa.me/447458107868', '_blank')}
                    >
                        <MdOutlineWhatsapp size={18} className="text-leadrs-text-soft" />
                        En savoir plus sur cette offre
                    </button>

                    <button
                        className="flex items-center justify-center gap-2 rounded-lg bg-leadrs-brand px-5 py-3 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80"
                        onClick={() => nav('/create-order')}
                    >
                        <MdAdd size={18} />
                        Faire une demande de leads
                    </button>

                    {industryInfo?.forWho && (
                        <div className="rounded-xl border border-leadrs-stroke bg-white p-4">
                            <div className="mb-2 flex items-center gap-2">
                                <TbTargetArrow size={16} className="text-leadrs-brand" />
                                <p className="font-geist text-sm font-semibold text-leadrs-text">
                                    Pour qui sont ces leads ?
                                </p>
                            </div>
                            <p className="font-geist text-sm text-leadrs-text-soft whitespace-pre-line">
                                {capitalizeFirstLetter(industryInfo.forWho)}
                            </p>
                        </div>
                    )}
                </div>

                {/* Right column — offer details */}
                <div className="flex-1">
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <p className="font-geist text-sm text-leadrs-text-soft">{offer?.subIndustry}</p>
                        <h1 className="font-crimson text-4xl font-semibold leading-tight text-leadrs-text">
                            {offer?.industry}
                        </h1>
                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">#{offer?.offerBuyerNumber}</p>

                        <hr className="my-5 border-leadrs-stroke" />

                        {/* Status blocks */}
                        <div className="flex flex-col gap-3">
                            {offer?.status === 'on_market' && user && !user.isValidated && (
                                <InfoBox
                                    icon={<MdDisplaySettings size={18} />}
                                    title="En attente de validation"
                                    description="Votre compte doit etre valide par un administrateur avant la publication."
                                    palette="yellow"
                                />
                            )}
                            {offer?.status === 'on_market' && (
                                <InfoBox
                                    icon={<MdOutlinePayment size={18} />}
                                    title="En attente de paiement"
                                    description={`Vous pouvez commander ${offer?.weeklyQuantity} leads a ${offer?.buyerPrice?.toFixed(2)}€ l'unite. Votre campagne commence une fois la commande payee.`}
                                    palette="yellow"
                                />
                            )}
                            {offer?.status === 'on_market' && (
                                <>
                                    <button
                                        className="w-full rounded-xl bg-leadrs-brand px-5 py-3 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80"
                                        onClick={async () => {
                                            if (isGuest) {
                                                setShowSignUpModal(true);
                                            } else {
                                                const { data } = await axios.post(
                                                    `${CONFIG.apiUrl}/stripe/checkout/${offer?._id}`,
                                                    {},
                                                    { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }
                                                );
                                                window.location.href = data.url;
                                            }
                                        }}
                                    >
                                        Payer par carte {(offer?.buyerPrice?.toFixed(2) * offer?.weeklyQuantity)?.toFixed(2)}€
                                    </button>
                                    {!isGuest && (
                                        <PayWithWalletButton
                                            amount={offer?.buyerPrice * offer?.weeklyQuantity}
                                            payUrl={`${CONFIG.apiUrl}/offer/${offer?._id}/pay-with-wallet`}
                                            successRedirect="/admin/paiement-success"
                                        />
                                    )}
                                </>
                            )}
                            {offer?.status === 'running' && (
                                <>
                                    <p className="font-geist text-sm text-leadrs-text-soft">
                                        Delai de livraison : jusqu'a 2 semaines apres reception du paiement.
                                    </p>
                                    <InfoBox
                                        icon={<MdOutlineRocketLaunch size={18} />}
                                        title="Mission en cours"
                                        description={`Vous avez deja recu ${offer?.leadsGenerated} leads, soit ${(offer?.leadsGenerated * offer?.buyerPrice?.toFixed(2))?.toFixed(2)}€.`}
                                        palette="green"
                                    />
                                </>
                            )}
                            {offer?.status === 'done' && (
                                <InfoBox
                                    icon={<MdDoneOutline size={18} />}
                                    title="Mission terminee"
                                    description={`Vous avez genere ${offer?.leadsGenerated} leads pour un total de ${(offer?.leadsGenerated * offer?.buyerPrice?.toFixed(2))?.toFixed(2)}€.`}
                                    palette="neutral"
                                />
                            )}
                        </div>

                        <hr className="my-5 border-leadrs-stroke" />

                        {/* Offer details list */}
                        <p className="mb-3 font-geist text-sm font-semibold text-leadrs-text">Details de l'offre</p>
                        {industryInfo?.description && (
                            <p className="mb-4 font-geist text-sm text-leadrs-text-soft">{industryInfo.description}</p>
                        )}
                        <div className="space-y-3">
                            {[
                                { label: 'Prix unitaire', value: offer?.buyerPrice?.toFixed(2) ? `${offer?.buyerPrice?.toFixed(2)} €` : null },
                                { label: 'Quantite', value: offer?.weeklyQuantity ? `${offer?.weeklyQuantity} leads` : null },
                                { label: 'Champs de donnees', value: offer?.fields?.join(', ') || null },
                                { label: "Canaux d'acquisition", value: offer?.channels?.join(', ') || null },
                                { label: 'Besoin', value: offer?.type || null },
                                { label: 'Departements', value: offer?.regions?.join(', ') || null },
                            ].filter(row => row.value).map(({ label, value }) => (
                                <div key={label} className="flex items-start justify-between gap-4">
                                    <p className="font-geist text-sm font-medium text-leadrs-text whitespace-nowrap">{label}</p>
                                    <p className="font-geist text-sm text-leadrs-text-soft text-right">{value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <SignUpModal isOpen={showSignUpModal} onClose={() => setShowSignUpModal(false)} />
        </div>
    );
};

export default BuyerMarketPlacePage;
